/**
 * Hook for auto-downloading documents when trips become active
 * 
 * Monitors trips and automatically downloads documents for trips
 * that are within 7 days of their start date.
 */

import { useEffect, useRef } from 'react';
import { autoDownloadForActiveTrip } from '@/services/offlineDocuments';
import type { Document } from '@/services/documentVault';
import type { Trip } from '@/services/trips';

type UseAutoDownloadDocumentsOptions = {
  trips: Trip[];
  getDocumentsForTrip: (tripId: string) => Promise<Document[]>;
  enabled?: boolean;
};

/**
 * Hook to auto-download documents for active trips
 * 
 * @param trips - Array of trips to monitor
 * @param getDocumentsForTrip - Function to fetch documents for a trip
 * @param enabled - Whether auto-download is enabled (default: true)
 */
export function useAutoDownloadDocuments({
  trips,
  getDocumentsForTrip,
  enabled = true,
}: UseAutoDownloadDocumentsOptions): void {
  const processedTripsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const processTrips = async () => {
      for (const trip of trips) {
        // Skip if already processed
        if (processedTripsRef.current.has(trip.id)) {
          continue;
        }

        // Check if trip is active (within 7 days)
        const now = new Date();
        const tripStartDate = new Date(trip.startDate);
        const diffTime = tripStartDate.getTime() - now.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (diffDays >= 0 && diffDays <= 7) {
          try {
            // Get documents for this trip
            const documents = await getDocumentsForTrip(trip.id);
            
            if (documents.length > 0) {
              // Auto-download documents
              await autoDownloadForActiveTrip(trip.id, tripStartDate, documents);
              processedTripsRef.current.add(trip.id);
            }
          } catch (error) {
            console.error(`[useAutoDownloadDocuments] Error processing trip ${trip.id}:`, error);
          }
        }
      }
    };

    processTrips();
  }, [trips, getDocumentsForTrip, enabled]);
}

